
import { computed, defineComponent, onMounted, ref, Ref } from 'vue'
import ContentLayout from '@/views/layout/ContentLayout.vue'
import { useStore } from 'vuex'
import { callApiWrapper } from '@/views/callApiWrapper'
import { keieiJohoRepositories } from '@/repositories/keiei-joho/keieiJohoRepositories'
import ResponseResultAlert from '@/views/components/ResponseResultAlert.vue'
import {
  CorporationInfoPick,
  FacilityInfoPick,
  FacilityInfoPickPick,
  ResponseShokushuRegisterLayout,
  ResponseShokushuShukeiData,
  ShokushuKmkType
} from '@/repositories/keiei-joho/shokushu/data'
import ShokushuRegisterItem from '@/views/keiei-joho/keiei-info/ShokushuRegisterItem.vue'
import { shokushuTypeLabels, workTypeLabels } from '@/repositories/keiei-joho/data'
import { hospitalClinicKbnType } from '@/views/corporation-info/types'

const repository = keieiJohoRepositories.shokushuRegister

// ※外部システム側で入力チェックされる為、使わなかった入力チェック。念のため、残しておく。
// 入力チェック
function inputCheck (shokushuItems: ShokushuKmkType[], requestError: Ref<string | null>) {
  // (shokushuType, workType) ごとにグループ化
  const grouped = new Map<string, ShokushuKmkType[]>()
  for (const item of shokushuItems) {
    const key = `${item.shokushuType}::${item.workType}`
    if (!grouped.has(key)) {
      grouped.set(key, [])
    }
    grouped.get(key)!.push(item)
  }

  // 各グループ内で集計チェック
  for (const [key, items] of grouped.entries()) {
    let ninzuKin = 0
    let otherKin = 0

    for (const item of items) {
      const kinValue = item.kin ?? 0
      if (item.isNinzuKmk) {
        ninzuKin += kinValue
      } else {
        otherKin += kinValue
      }
    }

    // 片方が1以上、もう片方が0なら false
    const ninzuHasValue = ninzuKin > 0
    const otherHasValue = otherKin > 0
    if ((ninzuHasValue && !otherHasValue) || (!ninzuHasValue && otherHasValue)) {
      const shokushuType = items[0].shokushuType
      const workType = items[0].workType

      // 和名に変換
      const shokushuLabel = shokushuTypeLabels[shokushuType]
      const workTypeLabel = workTypeLabels[workType]
      requestError.value = `「${shokushuLabel}」の「${workTypeLabel}」は、給与総額と人数の片方だけが入力されています。両方入力してください。`

      return false
    }
  }

  return true
}

export default defineComponent({
  components: {
    ShokushuRegisterItem,
    ResponseResultAlert,
    ContentLayout
  },
  setup () {
    const loading = ref(false)
    const isSucceedRequest = ref(false)
    const requestError = ref<string | null>(null)
    const hasError = ref<boolean>(false)
    const store = useStore()

    const accountingStartDate = store.state.accountingStartDate
    const accountingEndDate = store.state.accountingEndDate

    // セレクトボックス用の施設情報
    const facilityPicks = ref<FacilityInfoPickPick[]>([])
    const selectedFacility = ref<FacilityInfoPickPick | null>(null)

    // 法人情報、施設情報
    const corporationInfo = ref<CorporationInfoPick | null>(null)
    const facilityInfo = ref<FacilityInfoPick | null>(null)

    // 科目情報
    const shokushuItems = ref<ShokushuKmkType[]>([])

    // 帳票部分の表示可否
    const isShowRegisterItem = ref<boolean>(false)

    // 初期値設定
    onMounted(async () => {
      loading.value = true
      await callApiWrapper(requestError, async () => {
        const hasData = await repository.checkDataExists()
        if (!hasData.hasCorporationInfo) {
          requestError.value = '法人情報が登録されていません。法人情報画面から登録してください。'
          loading.value = false
          hasError.value = true
          return
        }
        if (!hasData.hasFacilityInfo) {
          requestError.value = '施設情報が登録されていません。法人情報画面から登録してください。'
          loading.value = false
          hasError.value = true
          return
        }
        if (!hasData.hasSettingData) {
          requestError.value = '科目設定が行われていません。職種別設定画面から登録してください。'
          loading.value = false
          hasError.value = true
          return
        }
        facilityPicks.value = await repository.getFacilityInfoPick()
      })
      loading.value = false
    })

    /**
     * レスポンス結果の非表示
     */
    const onDismissAlert = () => {
      isSucceedRequest.value = false
      requestError.value = null
    }

    /**
     * 施設情報セレクトボックスの変更時、選択施設に関連するデータを取得
     */
    const onFacilityChange = async () => {
      onDismissAlert()
      loading.value = true
      isShowRegisterItem.value = false

      const facility = selectedFacility.value
      if (facility !== null) {
        const kbn = facilityPicks.value.find(v => v.facilityKey === facility.facilityKey)?.hospitalClinicKbn
        if (kbn === hospitalClinicKbnType.NonReportable) {
          requestError.value = '選択施設の病院・診療所区分に「様式３（報告対象外の医療法人）」が選択されているため、本機能はご利用できません。'
          loading.value = false
          return
        }
        await callApiWrapper(requestError, async () => {
          const data = await repository.getKmkTypes(facility.facilityKey)
          shokushuItems.value = data.kmkTypes
          corporationInfo.value = data.corporationInfo
          facilityInfo.value = data.facilityInfo
          isShowRegisterItem.value = true
        })
      }
      loading.value = false
    }

    /**
     * 登録ボタン
     */
    const onClickedRegister = async () => {
      onDismissAlert()
      loading.value = true

      await callApiWrapper(requestError, async () => {
        const facilityKey = selectedFacility.value?.facilityKey ?? ''
        await repository.save(facilityKey, shokushuItems.value)
        isSucceedRequest.value = true
      })
      loading.value = false
    }

    /**
     * 再集計ボタン
     */
    const onClickedRecalculate = async (callback: (data: ResponseShokushuRegisterLayout) => void) => {
      onDismissAlert()
      loading.value = true
      await callApiWrapper(requestError, async () => {
        const facilityKey = selectedFacility.value?.facilityKey ?? ''
        const data = await repository.recalculate(facilityKey)
        callback(data)
      })
      loading.value = false
    }

    /**
     * 人数複写ボタン
     */
    const onClickedCopy = async (callback: (data: ResponseShokushuShukeiData[]) => void) => {
      const result = window.confirm('直近の期末日で登録されているデータから「人数（人）」列を複写します。よろしいですか？')
      if (!result) {
        return
      }

      onDismissAlert()
      loading.value = true
      await callApiWrapper(requestError, async () => {
        const facilityKey = selectedFacility.value?.facilityKey ?? ''
        const data = await repository.copy(facilityKey)
        callback(data)
      })
      loading.value = false
    }

    return {
      loading,
      isSucceedRequest,
      requestError,
      hasError,

      facilityPicks,
      selectedFacility,
      shokushuItems,
      isShowRegisterItem,

      corporationInfo,
      facilityInfo,

      accountingStartDate,
      accountingEndDate,

      onDismissAlert,
      onFacilityChange,
      onClickedRegister,
      onClickedRecalculate,
      onClickedCopy
    }
  }
})
